import React, { useState, useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import { supabase } from './supabaseClient';
import './ContactPage.css';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Get current user ID if logged in
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setCurrentUserId(user.id);
        }
      } catch (error) {
        console.log('No user logged in or error getting user:', error);
        // It's okay if user is not logged in - contact form can be submitted without login
      }
    };

    getCurrentUser();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    }
    
    if (!formData.subject.trim()) {
      errors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);
    
    try {
      // Prepare data for Supabase
      const contactData = {
        user_id: currentUserId || null, // Optional: link to authenticated user
        full_name: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        status: 'new', // Default status
        priority: 'normal' // Default priority
      };

      // Insert into Supabase contact_messages table
      const { data, error } = await supabase
        .from('contact_messages')
        .insert([contactData])
        .select();

      if (error) {
        throw error;
      }

      // Success - reset form and show success message
      console.log('Contact message saved successfully:', data);
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setIsSubmitting(false);
      setSubmitError(
        error.message || 'Failed to send message. Please try again later.'
      );
      
      // Reset error message after 5 seconds
      setTimeout(() => {
        setSubmitError(null);
      }, 5000);
    }
  };

  return (
    <div className="contact-page">
      <Header />
      
      {/* Hero Section */}
      <section className="contact-hero-section">
        <div className="page-container">
          <div className="contact-hero-content">
            <div className="contact-hero-icon">✈️</div>
            <h1 className="contact-hero-title">Contact Us</h1>
            <p className="contact-hero-subtitle">
              We're here to help with your bookings, payments, and travel inquiries.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="contact-main-section">
        <div className="page-container">
          <div className="contact-layout">
            {/* Contact Information Panel */}
            <div className="contact-info-section">
              <div className="contact-info-card">
                <h2 className="contact-info-title">Get in Touch</h2>
                <p className="contact-info-subtitle">
                  Reach out to us through any of these channels
                </p>
                
                <div className="contact-methods">
                  <div className="contact-method-card">
                    <div className="contact-method-icon">📞</div>
                    <div className="contact-method-content">
                      <h3 className="contact-method-label">Phone</h3>
                      <p className="contact-method-value">+234 800 123 4567</p>
                      <p className="contact-method-note">Nigeria only</p>
                    </div>
                  </div>

                  <div className="contact-method-card">
                    <div className="contact-method-icon">✉️</div>
                    <div className="contact-method-content">
                      <h3 className="contact-method-label">Email</h3>
                      <p className="contact-method-value">support@symbolfly.com</p>
                      <p className="contact-method-note">We respond within 24 hours</p>
                    </div>
                  </div>

                  <div className="contact-method-card">
                    <div className="contact-method-icon">📍</div>
                    <div className="contact-method-content">
                      <h3 className="contact-method-label">Office Address</h3>
                      <p className="contact-method-value">Lagos, Nigeria</p>
                      <p className="contact-method-note">Visit us during business hours</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form Panel */}
            <div className="contact-form-section">
              <div className="contact-form-card">
                <h2 className="contact-form-title">Send us a Message</h2>
                <p className="contact-form-subtitle">
                  Fill out the form below and we'll get back to you as soon as possible
                </p>

                {submitSuccess && (
                  <div className="form-success-message">
                    ✓ Thank you! Your message has been sent successfully. We'll get back to you soon.
                  </div>
                )}

                {submitError && (
                  <div className="form-error-message">
                    ⚠️ {submitError}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-group">
                    <label htmlFor="fullName" className="form-label">
                      Full Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className={`form-input ${formErrors.fullName ? 'error' : ''}`}
                      placeholder="Enter your full name"
                    />
                    {formErrors.fullName && (
                      <span className="error-message">{formErrors.fullName}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email" className="form-label">
                      Email <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`form-input ${formErrors.email ? 'error' : ''}`}
                      placeholder="your.email@example.com"
                    />
                    {formErrors.email && (
                      <span className="error-message">{formErrors.email}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">
                      Phone Number <span className="required">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`form-input ${formErrors.phone ? 'error' : ''}`}
                      placeholder="+234 801 234 5678"
                    />
                    {formErrors.phone && (
                      <span className="error-message">{formErrors.phone}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="subject" className="form-label">
                      Subject <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className={`form-input ${formErrors.subject ? 'error' : ''}`}
                      placeholder="What is this regarding?"
                    />
                    {formErrors.subject && (
                      <span className="error-message">{formErrors.subject}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="message" className="form-label">
                      Message <span className="required">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      className={`form-textarea ${formErrors.message ? 'error' : ''}`}
                      placeholder="Tell us how we can help..."
                      rows="6"
                    />
                    {formErrors.message && (
                      <span className="error-message">{formErrors.message}</span>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="submit-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Support Hours Strip */}
          <div className="support-hours-strip">
            <div className="support-hours-icon">🕐</div>
            <div className="support-hours-content">
              <h3 className="support-hours-title">Support Hours</h3>
              <p className="support-hours-text">
                Monday – Sunday: 8:00 AM – 10:00 PM WAT
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;








